import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    DialogBody,
    DialogHeader,
    LoadingPanel,
    Stepper,
    StepperStep,
    toast,
} from '@gitbook/spine-react';

import { IntegrationDialogAside } from '../../../Components';
import { useSetAnalyticsScreen } from '../../../navigation';
import { ConfigurationStep, InstallationStep, SyncProgressStep } from './Steps';
import type { GitSyncInstallationFlowProps } from './types';

type GitSyncInstallStep =
    | {
          step: 'install_git_integration';
      }
    | {
          step: 'configure_space_installation';
          integration: api.Integration;
      }
    | {
          step: 'sync_progress';
          integration: api.Integration;
      };

const gitSyncInstallSteps: { id: GitSyncInstallStep['step']; title: string }[] = [
    { id: 'install_git_integration', title: 'Pick a provider' },
    { id: 'configure_space_installation', title: 'Configure the integration' },
    { id: 'sync_progress', title: 'Sync progress' },
];

/**
 * Dialog content with flow to setup a new GitSync in the context of a space.
 */
export function GitSyncInstallationFlow(
    props: GitSyncInstallationFlowProps & {
        /** Callback when the flow is done. */
        onComplete: () => void;
    }
) {
    const { organization, space, onComplete } = props;

    const [currentStep, setCurrentStep] = React.useState<GitSyncInstallStep>({
        step: 'install_git_integration',
    });

    useSetAnalyticsScreen(
        analytics.createSpaceEvent(
            { key: space.id, organization: space.organization },
            {
                screen: 'space_gitsync_installation_flow',
            }
        )
    );
    return (
        <div className="flex h-full max-md:flex-col">
            <IntegrationDialogAside
                integration={'integration' in currentStep ? currentStep.integration : undefined}
            />
            <div className="flex h-full flex-1 flex-col">
                <DialogHeader>
                    <Stepper<GitSyncInstallStep['step']>
                        value={currentStep.step}
                        onChange={() => {
                            // noop at the moment, we shouldn't be able to change steps for now
                        }}
                    >
                        {gitSyncInstallSteps.map((step) => (
                            <StepperStep key={step.id} id={step.id} title={step.title} />
                        ))}
                    </Stepper>
                </DialogHeader>

                <DialogBody>
                    <div className="flex-grow-0">
                        <React.Suspense fallback={<LoadingPanel size="large" />}>
                            {currentStep.step === 'install_git_integration' ? (
                                <InstallationStep
                                    organization={organization}
                                    space={space}
                                    onNext={(integration) => {
                                        if (integration.configurations?.space) {
                                            setCurrentStep({
                                                step: 'configure_space_installation',
                                                integration,
                                            });
                                        } else {
                                            toast.error(
                                                'Failed to install the integration in this space',
                                                null
                                            );
                                        }
                                    }}
                                />
                            ) : null}
                            {currentStep.step === 'configure_space_installation' ? (
                                <ConfigurationStep
                                    organization={organization}
                                    integration={currentStep.integration}
                                    space={space}
                                    onNext={() => {
                                        setCurrentStep({
                                            step: 'sync_progress',
                                            integration: currentStep.integration,
                                        });
                                    }}
                                />
                            ) : null}
                            {currentStep.step === 'sync_progress' ? (
                                <SyncProgressStep
                                    organization={organization}
                                    space={space}
                                    onNext={onComplete}
                                />
                            ) : null}
                        </React.Suspense>
                    </div>
                </DialogBody>
            </div>
        </div>
    );
}
